import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../components/ui/card';
import { Badge } from '../../../components/ui/badge';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Skeleton } from '../../../components/ui/skeleton';

import { 
  CheckCircle, 
  XCircle, 
  Clock, 
  GitBranch, 
  User, 
  Calendar,
  ExternalLink,
  Search,
  Filter
} from 'lucide-react';

/**
 * Componente de historial de deployments
 * Muestra lista detallada de deployments con filtros
 */
const DeploymentHistory = ({ deployments, isLoading, onRefresh }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  // Filtrar deployments
  const filteredDeployments = deployments?.filter(deployment => {
    const matchesSearch = !searchTerm || 
      deployment.commit?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      deployment.branch?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      deployment.author?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      deployment.creator?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      deployment.projectName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      deployment.environment?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      deployment.target?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || deployment.status === statusFilter;
    
    return matchesSearch && matchesStatus;
  }) || [];

  // Obtener icono de estado
  const getStatusIcon = (status) => {
    switch (status) {
      case 'success':
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'error':
        return <XCircle className="h-4 w-4 text-red-500" />;
      case 'building':
        return <Clock className="h-4 w-4 text-yellow-500 animate-pulse" />;
      default:
        return <Clock className="h-4 w-4 text-gray-500" />;
    }
  };

  // Obtener badge de estado
  const getStatusBadge = (status) => {
    const variants = {
      success: 'default',
      error: 'destructive',
      building: 'secondary',
      cancelled: 'outline'
    };

    const labels = {
      success: 'Success',
      error: 'Failed',
      building: 'Building',
      cancelled: 'Cancelled'
    };

    return (
      <Badge variant={variants[status] || 'outline'}>
        {labels[status] || status}
      </Badge>
    );
  };

  // Formatear duración
  const formatDuration = (duration) => {
    if (!duration) return 'N/A';
    
    if (typeof duration === 'string') return duration;
    
    const minutes = Math.floor(duration / 60);
    const seconds = duration % 60;
    
    if (minutes > 0) {
      return `${minutes}m ${seconds}s`;
    }
    return `${seconds}s`;
  };

  // Formatear fecha
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  const getEnvironmentLabel = (environment) => {
    const normalizedEnvironment = String(environment || '').toLowerCase();

    if (normalizedEnvironment === 'production') return 'Producción';
    if (normalizedEnvironment === 'preview') return 'Preview';
    if (normalizedEnvironment === 'development') return 'Desarrollo';

    return environment || 'N/A';
  };

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <Skeleton className="h-6 w-48" />
          <Skeleton className="h-4 w-64" />
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="flex items-center space-x-4 p-4 border rounded-lg">
                <Skeleton className="h-8 w-8 rounded-full" />
                <div className="flex-1 space-y-2">
                  <Skeleton className="h-4 w-32" />
                  <Skeleton className="h-3 w-48" />
                </div>
                <Skeleton className="h-6 w-16" />
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle>Historial de Deployments</CardTitle>
            <CardDescription>
              Lista completa de deployments con detalles y estado
            </CardDescription>
          </div>
          <Button onClick={onRefresh} variant="outline" size="sm">
            Actualizar
          </Button>
        </div>

        {/* Filtros */}
        <div className="flex items-center space-x-4 mt-4">
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Buscar por commit, branch o autor..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
          
          <div className="flex items-center space-x-2">
            <Filter className="h-4 w-4 text-muted-foreground" />
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-3 py-2 border rounded-md text-sm"
            >
              <option value="all">Todos los estados</option>
              <option value="success">Exitosos</option>
              <option value="error">Fallidos</option>
              <option value="building">En construcción</option>
            </select>
          </div>
        </div>
      </CardHeader>

      <CardContent>
        {filteredDeployments.length > 0 ? (
          <div className="space-y-4">
            {filteredDeployments.map((deployment, index) => (
              <div key={index} className="flex items-center space-x-4 p-4 border rounded-lg hover:bg-muted/50 transition-colors">
                {/* Estado */}
                <div className="flex items-center space-x-2">
                  {getStatusIcon(deployment.status)}
                  {getStatusBadge(deployment.status)}
                </div>

                {/* Información principal */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center space-x-2 mb-1">
                    <GitBranch className="h-4 w-4 text-muted-foreground" />
                    <span className="font-medium">{deployment.branch || 'main'}</span>
                    {deployment.commit && (
                      <span className="font-mono text-sm text-muted-foreground">
                        {deployment.commit.substring(0, 8)}
                      </span>
                    )}
                  </div>
                  
                  <div className="flex items-center space-x-4 text-sm text-muted-foreground">
                    <div className="flex items-center space-x-1">
                      <span>Proyecto:</span>
                      <span className="font-medium text-foreground">
                        {deployment.projectName || deployment.projectId || 'N/A'}
                      </span>
                    </div>

                    <div className="flex items-center space-x-1">
                      <span>Ambiente:</span>
                      <span className="font-medium text-foreground">
                        {getEnvironmentLabel(deployment.environment || deployment.target)}
                      </span>
                    </div>

                    {deployment.author && (
                      <div className="flex items-center space-x-1">
                        <User className="h-3 w-3" />
                        <span>{deployment.author}</span>
                      </div>
                    )}

                    {!deployment.author && deployment.creator && (
                      <div className="flex items-center space-x-1">
                        <User className="h-3 w-3" />
                        <span>{deployment.creator}</span>
                      </div>
                    )}
                    
                    <div className="flex items-center space-x-1">
                      <Calendar className="h-3 w-3" />
                      <span>{formatDate(deployment.createdAt)}</span>
                    </div>
                    
                    {deployment.duration && (
                      <span>Duración: {formatDuration(deployment.duration)}</span>
                    )}
                  </div>

                  {deployment.message && (
                    <p className="text-sm text-muted-foreground mt-1 truncate">
                      {deployment.message}
                    </p>
                  )}
                </div>

                {/* Acciones */}
                <div className="flex items-center space-x-2">
                  {deployment.url && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => window.open(deployment.url, '_blank')}
                    >
                      <ExternalLink className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center text-muted-foreground py-8">
            {searchTerm || statusFilter !== 'all' 
              ? 'No se encontraron deployments con los filtros aplicados'
              : 'No hay deployments disponibles'
            }
          </div>
        )}

        {/* Estadísticas */}
        {deployments && deployments.length > 0 && (
          <div className="mt-6 pt-6 border-t">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 text-center">
              <div>
                <p className="text-2xl font-bold text-[var(--yool-success)]">
                  {deployments.filter(d => d.status === 'success').length}
                </p>
                <p className="text-sm text-muted-foreground">Exitosos</p>
              </div>
              
              <div>
                <p className="text-2xl font-bold text-[var(--yool-error)]">
                  {deployments.filter(d => d.status === 'error').length}
                </p>
                <p className="text-sm text-muted-foreground">Fallidos</p>
              </div>
              
              <div>
                <p className="text-2xl font-bold text-yellow-600">
                  {deployments.filter(d => d.status === 'building').length}
                </p>
                <p className="text-sm text-muted-foreground">En construcción</p>
              </div>
              
              <div>
                <p className="text-2xl font-bold">
                  {deployments.length}
                </p>
                <p className="text-sm text-muted-foreground">Total</p>
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default DeploymentHistory;
